import { useState } from 'react';
import type { FormEvent } from 'react';

import { Avviso, Campo, Pulsante } from '../../componenti';
import { ErroreApi, ErroreRete, api } from '../../lib/api';
import { LUNGHEZZA_MINIMA_PASSWORD } from '../../lib/password';
import { useSessione } from '../../stato/Sessione';

/**
 * 6f — primo accesso, cambio password obbligatorio.
 *
 * Finché `primo_accesso = 1` ogni rotta protetta riporta qui: senza una password personale
 * l'area riservata resta chiusa. I tre requisiti si spuntano mentre si scrive; il server
 * li verifica comunque per conto proprio.
 */

export function PrimoAccesso() {
  const { profilo, ricarica } = useSessione();
  const [pdf, setPdf] = useState('');
  const [nuova, setNuova] = useState('');
  const [ripeti, setRipeti] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  const abbastanzaLunga = nuova.length >= LUNGHEZZA_MINIMA_PASSWORD;
  const diversa = nuova.length > 0 && nuova !== pdf;
  const coincidono = nuova.length > 0 && nuova === ripeti;
  const valida = abbastanzaLunga && diversa && coincidono && pdf.length > 0;

  async function invia(e: FormEvent) {
    e.preventDefault();
    if (!valida || inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      await api.put('/auth/password', {
        password_attuale: pdf,
        password_nuova: nuova,
      });
      await ricarica();
    } catch (err) {
      if (err instanceof ErroreRete) setErrore('Serve la connessione. Riprova.');
      else if (err instanceof ErroreApi) setErrore(err.message);
      else throw err;
      setInCorso(false);
    }
  }

  const nome = profilo?.tiratore?.nome ?? profilo?.username ?? '';

  return (
    <div className="accesso accesso--colonna" data-densita="tattile">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 12 }}>
        <div className="etichetta-sezione">Primo accesso</div>
        <h1 style={{ fontSize: 26 }}>Benvenuto, {nome}</h1>
      </div>

      <div className="accesso__scheda">
        <div style={{ fontWeight: 600, fontSize: 17 }}>Scegli la tua password</div>
        <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--testo-secondario)' }}>
          La password stampata sul PDF serve solo per il primo ingresso. Sostituiscila ora:
          da qui in avanti userai la nuova.
        </p>

        {errore ? <Avviso tono="errore" titolo={errore} /> : null}

        <form onSubmit={invia} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Campo
            etichetta="Password del PDF"
            type="password"
            autoComplete="current-password"
            value={pdf}
            onChange={(e) => setPdf(e.target.value)}
          />
          <Campo
            etichetta="Nuova password"
            type="password"
            autoComplete="new-password"
            value={nuova}
            onChange={(e) => setNuova(e.target.value)}
          />
          <Campo
            etichetta="Ripeti la nuova password"
            type="password"
            autoComplete="new-password"
            value={ripeti}
            onChange={(e) => setRipeti(e.target.value)}
          />

          <div className="requisiti">
            <Requisito soddisfatto={abbastanzaLunga}>
              Almeno {LUNGHEZZA_MINIMA_PASSWORD} caratteri
            </Requisito>
            <Requisito soddisfatto={diversa}>Diversa da quella del PDF</Requisito>
            <Requisito soddisfatto={coincidono}>Le due password coincidono</Requisito>
          </div>

          <Pulsante type="submit" largo grande disabled={!valida || inCorso}>
            Salva ed entra
          </Pulsante>
        </form>
      </div>

      <div className="accesso__nota">
        Non è possibile saltare questo passaggio: senza una password personale l’area
        riservata resta chiusa.
      </div>
    </div>
  );
}

function Requisito({
  soddisfatto,
  children,
}: {
  soddisfatto: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={`requisito${soddisfatto ? ' requisito--soddisfatto' : ''}`}>
      <span className="requisito__segno" aria-hidden="true">
        {soddisfatto ? '✓' : ''}
      </span>
      <span>{children}</span>
    </div>
  );
}
